import React, { useState, useEffect, useMemo } from 'react';
import { api } from '@/contexts/AuthContext';
import { Search, Phone, Mail, GitBranch, LayoutGrid, ChevronDown, ChevronRight } from 'lucide-react';

export default function DirectoryPage() {
  const [employees, setEmployees] = useState([]);
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [view, setView] = useState('tree');
  const [totalCount, setTotalCount] = useState(0);
  const [collapsed, setCollapsed] = useState({});

  useEffect(() => {
    Promise.all([api.get('/directory'), api.get('/directory/tree')])
      .then(([listRes, treeRes]) => {
        setEmployees(listRes.data.employees || []);
        setTree(treeRes.data.tree || []);
        setTotalCount(treeRes.data.total || 0);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const filteredList = useMemo(() => {
    if (!search) return employees;
    const s = search.toLowerCase();
    return employees.filter(e =>
      (e.name || '').toLowerCase().includes(s) ||
      (e.designation || '').toLowerCase().includes(s) ||
      (e.phone || '').includes(s) ||
      (e.email || '').toLowerCase().includes(s)
    );
  }, [employees, search]);

  // Flatten tree into array with depth info for non-recursive rendering
  const flatTree = useMemo(() => {
    const result = [];
    const flatten = (nodes, depth) => {
      for (const node of nodes) {
        const hasKids = (node.children || []).length > 0;
        const isCollapsed = collapsed[node.id] === true;
        result.push({ ...node, depth, hasChildren: hasKids, isCollapsed });
        if (hasKids && !isCollapsed) {
          flatten(node.children, depth + 1);
        }
      }
    };

    const filterNodes = (nodes) => {
      if (!search) return nodes;
      const s = search.toLowerCase();
      return nodes.reduce((acc, node) => {
        const match = (node.name || '').toLowerCase().includes(s) ||
                      (node.designation || '').toLowerCase().includes(s);
        const filteredKids = filterNodes(node.children || []);
        if (match || filteredKids.length > 0) {
          acc.push({ ...node, children: filteredKids });
        }
        return acc;
      }, []);
    };

    flatten(filterNodes(tree), 0);
    return result;
  }, [tree, search, collapsed]);

  const toggleCollapse = (id) => {
    setCollapsed(prev => ({ ...prev, [id]: !prev[id] }));
  };

  if (loading) return (
    <div className="flex justify-center py-12">
      <div className="animate-spin w-8 h-8 border-2 border-[#1B5486] border-t-transparent rounded-full" />
    </div>
  );

  return (
    <div data-testid="directory-page" className="space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 font-heading tracking-tight">Employee Directory</h1>
          <p className="text-sm text-slate-500">{totalCount} employees</p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, role, phone..."
              className="pl-9 pr-3 py-2 border border-slate-200 rounded-md text-sm outline-none w-full sm:w-64 focus:border-[#1B5486]"
              data-testid="directory-search" />
          </div>
          <div className="flex border border-slate-200 rounded-md overflow-hidden self-start">
            <button onClick={() => setView('tree')} data-testid="view-tree-btn"
              className={`px-3 py-2 text-xs font-medium flex items-center gap-1 ${view === 'tree' ? 'bg-[#1B5486] text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}>
              <GitBranch size={12} /> Tree
            </button>
            <button onClick={() => setView('list')} data-testid="view-list-btn"
              className={`px-3 py-2 text-xs font-medium flex items-center gap-1 ${view === 'list' ? 'bg-[#1B5486] text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}>
              <LayoutGrid size={12} /> List
            </button>
          </div>
        </div>
      </div>

      {view === 'tree' ? (
        <div className="bg-white rounded-lg border border-slate-200 p-5 overflow-hidden" data-testid="tree-view">
          {flatTree.length === 0 ? (
            <div className="text-center py-12 text-sm text-slate-400">No employees found</div>
          ) : (
            <div className="space-y-0.5">
              {flatTree.map(node => (
                <div key={node.id} style={{ marginLeft: node.depth * 24 }} data-testid={`tree-node-${node.id}`}
                  className={`flex items-center gap-2 py-1.5 px-2 rounded-md hover:bg-slate-50 transition-colors ${node.depth === 0 ? 'bg-slate-50' : ''}`}>
                  {node.hasChildren ? (
                    <button onClick={() => toggleCollapse(node.id)} className="text-slate-400 hover:text-slate-600 flex-shrink-0">
                      {node.isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                    </button>
                  ) : (
                    <span className="w-[14px] flex-shrink-0" />
                  )}
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#1B5486] to-[#2d7ab8] flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {node.photo_url ? (
                      <img src={node.photo_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-white text-xs font-bold">{(node.name || '?')[0]}</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{node.name}</p>
                    <p className="text-[10px] text-slate-500 truncate">{node.designation || node.employee_type || ''}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {node.phone && (
                      <a href={`tel:${node.phone}`} className="text-slate-400 hover:text-[#1B5486]" data-testid={`call-${node.id}`} title={node.phone}>
                        <Phone size={13} />
                      </a>
                    )}
                    {node.email && (
                      <a href={`mailto:${node.email}`} className="text-slate-400 hover:text-[#1B5486]" title={node.email}>
                        <Mail size={13} />
                      </a>
                    )}
                  </div>
                  {node.hasChildren && <span className="text-[10px] text-slate-400 ml-1">{(node.children || []).length}</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="list-view">
          {filteredList.map(emp => (
            <div key={emp.id} className="bg-white rounded-lg border border-slate-200 p-4 hover:shadow-sm transition-all" data-testid={`emp-card-${emp.id}`}>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#1B5486] to-[#2d7ab8] flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {emp.photo_url ? (
                    <img src={emp.photo_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-white text-sm font-bold">{(emp.name || '?')[0]}</span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-semibold text-slate-900 truncate">{emp.name}</h3>
                  <p className="text-xs text-slate-500 truncate">{emp.designation || emp.employee_type || ''}</p>
                  {emp.department && <p className="text-[10px] text-slate-400">{emp.department}</p>}
                </div>
              </div>
              <div className="mt-3 space-y-1.5">
                {emp.phone && (
                  <a href={`tel:${emp.phone}`} className="flex items-center gap-2 text-xs text-slate-600 hover:text-[#1B5486]" data-testid={`card-call-${emp.id}`}>
                    <Phone size={12} className="text-slate-400" /> {emp.phone}
                  </a>
                )}
                {emp.email && (
                  <a href={`mailto:${emp.email}`} className="flex items-center gap-2 text-xs text-slate-600 hover:text-[#1B5486] truncate">
                    <Mail size={12} className="text-slate-400" /> {emp.email}
                  </a>
                )}
              </div>
            </div>
          ))}
          {filteredList.length === 0 && <div className="col-span-4 text-center py-12 text-sm text-slate-400">No employees found</div>}
        </div>
      )}
    </div>
  );
}
